import React, { useEffect, useRef, useState } from "react";
import { Eraser, Undo2, Check } from "lucide-react";
import { Btn } from "@/components/kit";

// Lightweight finger/mouse sketch canvas. Emits a PNG data URL via onSave.
export function SketchPad({ onSave, saving, saved }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const strokes = useRef([]); // for undo: array of ImageData snapshots
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    // fixed backing size for crisp export; CSS scales it
    c.width = 900; c.height = 560;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // faint grid to help orient the scene
    ctx.strokeStyle = "#e2e8f0"; ctx.lineWidth = 1;
    for (let x = 0; x <= c.width; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, c.height); ctx.stroke(); }
    for (let y = 0; y <= c.height; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(c.width, y); ctx.stroke(); }
    ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 3;
  }, []);

  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    strokes.current.push(ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height));
    if (strokes.current.length > 20) strokes.current.shift();
    drawing.current = true;
    const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y);
    setDirty(true);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke();
  };
  const end = () => { drawing.current = false; };
  const undo = () => {
    const snap = strokes.current.pop();
    if (snap) canvasRef.current.getContext("2d").putImageData(snap, 0, 0);
    if (!strokes.current.length) setDirty(false);
  };
  const clear = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    strokes.current = [];
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#e2e8f0"; ctx.lineWidth = 1;
    for (let x = 0; x <= c.width; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, c.height); ctx.stroke(); }
    for (let y = 0; y <= c.height; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(c.width, y); ctx.stroke(); }
    ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 3;
    setDirty(false);
  };
  const save = () => onSave(canvasRef.current.toDataURL("image/png"));

  return (
    <div data-testid="sketch-pad">
      <canvas ref={canvasRef}
        className="w-full touch-none rounded-[6px] border border-[hsl(var(--input))] bg-white cursor-crosshair"
        style={{ aspectRatio: "900 / 560" }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="sketch-canvas" />
      <div className="flex items-center gap-2 mt-2">
        <Btn type="button" variant="outline" onClick={undo} disabled={!dirty} data-testid="sketch-undo"><Undo2 className="h-4 w-4" /> Undo</Btn>
        <Btn type="button" variant="outline" onClick={clear} disabled={!dirty} data-testid="sketch-clear"><Eraser className="h-4 w-4" /> Clear</Btn>
        <Btn type="button" onClick={save} disabled={!dirty || saving} data-testid="sketch-save" className="ml-auto">{saved ? <><Check className="h-4 w-4" /> Saved</> : saving ? "Saving…" : "Save sketch"}</Btn>
      </div>
    </div>
  );
}
